import {
  Dropdown,
  Option,
  Radio,
  RadioGroup,
} from '@fluentui/react-components';
import {
  ClassService,
  ServiceType,
  Student,
  UserListSorting,
  getSortedUserLabel,
  sortUsersByPreference,
} from '@mnspro-cloud/services';
import { ClassBasicResponse } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { first } from 'lodash';
import moment from 'moment-timezone';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../Button';
import { LocalizedFormDatePicker } from '../../../LocalizedFormDatePicker/LocalizedFormDatePicker';
import { ResponsiveDialog } from '../../../ResponsiveDialog/ResponsiveDialog';
import './NewClassServiceDialog.scss';

export interface NewClassServiceDialogProps {
  isMobileView: boolean;
  isOpen: boolean;
  serviceDefinitions?: ServiceType[];
  students?: Student[];
  editModel?: ClassService;
  classes: ClassBasicResponse[];
  cancelButtonClick?: () => void;
  confirmButtonClick?: (services: ClassService) => void;
  preferenceSorting?: UserListSorting;
}

const emptyModel: ClassService = {
  content: '',
  end: moment(new Date()).add(1, 'week').toString(),
  groupId: '',
  start: new Date().toString(),
  students: [],
  title: '',
  definitionId: '',
  isActive: true,
};

enum ServiceTarget {
  Class,
  Course,
}

const createEmptyModel = (classes: ClassBasicResponse[]) => ({
  ...emptyModel,
  groupId: first(classes)?.id ?? '',
});

const getDefaultServiceTarget = (
  classes: ClassBasicResponse[],
  groupId: string,
) => {
  if (!groupId) {
    return ServiceTarget.Class;
  }
  return classes.some(c => c.id === groupId)
    ? ServiceTarget.Class
    : ServiceTarget.Course;
};

export const NewClassServiceDialog: FC<NewClassServiceDialogProps> = props => {
  const {
    isOpen,
    serviceDefinitions,
    students,
    editModel,
    classes,
    cancelButtonClick,
    confirmButtonClick,
    preferenceSorting,
  } = props;

  const { t } = useTranslation();

  const [classServiceModel, setClassServiceModel] = useState<ClassService>(
    editModel ?? createEmptyModel(classes),
  );

  const [serviceTarget, setServiceTarget] = useState<ServiceTarget>(
    getDefaultServiceTarget(classes, classServiceModel.groupId),
  );

  useEffect(() => {
    if (serviceTarget === ServiceTarget.Class) {
      if (classes.length === 0) {
        setServiceTarget(ServiceTarget.Course);
        return;
      }

      if (classes.some(c => c.id === classServiceModel.groupId)) {
        return;
      }

      var firstClass = first(classes)!!;
      handleGroupIdChange(firstClass.id);
      return;
    }

    // Set to default course id
    var currentIdIsClass = classes.some(c => c.id === editModel?.groupId);
    handleGroupIdChange(currentIdIsClass ? '' : editModel?.groupId ?? '');
  }, [serviceTarget, editModel]);

  useEffect(() => {
    if (editModel) {
      setClassServiceModel(editModel);
    } else {
      setClassServiceModel(createEmptyModel(classes));
    }

    setServiceTarget(
      getDefaultServiceTarget(classes, editModel?.groupId ?? ''),
    );
  }, [editModel, classes]);

  const handleConfirm = () => {
    confirmButtonClick?.(classServiceModel);
    setClassServiceModel(createEmptyModel(classes));
    setServiceTarget(getDefaultServiceTarget(classes, ''));
  };
  const handleCancel = () => {
    cancelButtonClick?.();
    setClassServiceModel(createEmptyModel(classes));
    setServiceTarget(getDefaultServiceTarget(classes, ''));
  };
  const handleStartChange = (date: Date) => {
    const start = moment(date);
    let end = moment(classServiceModel.end);

    if (start.isAfter(end)) {
      end = start.clone();
    }

    setClassServiceModel({
      ...classServiceModel,
      start: start.toISOString(),
      end: end.toISOString(),
    });
  };
  const handleEndChange = (date: Date) => {
    setClassServiceModel({ ...classServiceModel, end: date.toString() });
  };

  const handleDefinitionChange = (definitionId: string) => {
    const definition = serviceDefinitions?.find(d => d.id === definitionId);
    if (!definition) {
      return;
    }
    setClassServiceModel({
      ...classServiceModel,
      definitionId: definition.id,
      content: definition.description ?? '',
      title: definition.fullName,
    });
  };
  const handleStudentsChange = (selectedIds: string[]) => {
    setClassServiceModel({
      ...classServiceModel,
      students: (students ?? []).filter(s => selectedIds.includes(s.id)),
    });
  };

  const handleGroupIdChange = (groupId: string) => {
    setClassServiceModel({
      ...classServiceModel,
      groupId,
    });
  };

  const noServiceDefinition = (): boolean => !classServiceModel.definitionId;

  const noStudents = (): boolean => classServiceModel.students.length < 1;

  const areDatesValid = (): boolean =>
    moment(classServiceModel.start).isSameOrBefore(classServiceModel.end);

  const isValid = (): boolean => {
    return !noServiceDefinition() && areDatesValid() && !noStudents();
  };

  const errorLabel = !areDatesValid()
    ? t('classbook:course:course:class_service_container:create_class_service_dialog:end_date_before_start_date_error_message', { defaultValue: 'Das Enddatum muss vor dem Startdatum liegen.' })
    : '';

  const selectedClass =
    classes.find(c => c.id === classServiceModel.groupId) ?? first(classes);

  const selectedServiceDefinition = serviceDefinitions?.find(
    def => def.id === classServiceModel.definitionId,
  );

  const selectedStudentIds = classServiceModel.students.map(s => s.id);
  const selectedStudentLabels = classServiceModel.students
    .map(s => getSortedUserLabel(s, preferenceSorting ?? UserListSorting.FirstName))
    .join(', ');

  const headerText = t('classbook:course:course:class_service_container:create_class_service_dialog:header_new_class_service', { defaultValue: 'Neuer Dienst' });

  const contentElement = (
    <div className="flex flex-1 flex-col gap-2 contentWrapper">
      <div className="flex flex-col">
        <span>
          {t('classbook:absences:service_target_choose', { defaultValue: 'Dienst erstellen für:' })}
        </span>
        <RadioGroup
          layout="vertical"
          value={serviceTarget.toString()}
          onChange={(_, data) => {
            setServiceTarget(parseInt(data.value, 10) as ServiceTarget);
          }}
        >
          <div className="flex items-center">
            <Radio
              value={ServiceTarget.Class.toString()}
              label={t('class', { defaultValue: 'Klasse' })}
            />
            {serviceTarget === ServiceTarget.Class && classes.length > 1 && (
              <Dropdown
                appearance="filled-darker"
                placeholder={t('select_class', { defaultValue: 'Klasse auswählen' })}
                value={selectedClass?.name ?? ''}
                selectedOptions={selectedClass ? [selectedClass.id] : []}
                onOptionSelect={(_, data) => {
                  if (data.optionValue) {
                    handleGroupIdChange(data.optionValue);
                  }
                }}
              >
                {classes.map(item => (
                  <Option key={item.id} value={item.id} text={item.name}>
                    {item.name}
                  </Option>
                ))}
              </Dropdown>
            )}
          </div>
          <Radio
            value={ServiceTarget.Course.toString()}
            label={t('classbook:absences:current_course', { defaultValue: 'Aktueller Kurs' })}
          />
        </RadioGroup>
      </div>

      <div className="flex flex-col">
        <span>
          {t('classbook:course:course:class_service_container:create_class_service_dialog:service_type_label', { defaultValue: 'Diensttyp' })}
        </span>
        <Dropdown
          appearance="filled-darker"
          placeholder={t('classbook:course:course:class_service_container:create_class_service_dialog:service_type_placeholder', { defaultValue: 'Bitte Diensttyp auswählen' })}
          value={selectedServiceDefinition?.shortName ?? ''}
          selectedOptions={selectedServiceDefinition?.id ? [selectedServiceDefinition.id] : []}
          onOptionSelect={(_, data) => {
            if (data.optionValue) {
              handleDefinitionChange(data.optionValue);
            }
          }}
        >
          {(serviceDefinitions ?? []).map(def => (
            <Option key={def.id} value={def.id} text={def.shortName}>
              {def.shortName}
            </Option>
          ))}
        </Dropdown>
      </div>

      <div className="flex flex-col">
        <span>
          {t('classbook:course:course:class_service_container:create_class_service_dialog:start_label', { defaultValue: 'Start' })}
        </span>
        <LocalizedFormDatePicker
          onDateChange={(_, data) => {
            data?.value ? handleStartChange(data.value) : null;
          }}
          selectedDate={new Date(classServiceModel.start)}
          errorMessage={errorLabel}
        />
      </div>

      <div className="flex flex-col">
        <span>
          {t('classbook:course:course:class_service_container:create_class_service_dialog:end_label', { defaultValue: 'Ende' })}
        </span>
        <LocalizedFormDatePicker
          onDateChange={(_, data) => {
            data?.value ? handleEndChange(data.value) : null;
          }}
          selectedDate={new Date(classServiceModel.end)}
          minDate={new Date(classServiceModel.start)}
        />
      </div>

      <div className="flex flex-col">
        <span>
          {t('classbook:course:course:class_service_container:create_class_service_dialog:students_label', { defaultValue: 'Schüler' })}
        </span>
        <Dropdown
          appearance="filled-darker"
          multiselect
          placeholder={t('classbook:course:course:class_service_container:create_class_service_dialog:students_placeholder', { defaultValue: 'Bitte Schüler auswählen' })}
          className="studentDropdown"
          value={selectedStudentLabels}
          selectedOptions={selectedStudentIds}
          onOptionSelect={(_, data) => {
            handleStudentsChange(data.selectedOptions);
          }}
        >
          {sortUsersByPreference(
            students ?? [],
            preferenceSorting ?? UserListSorting.FirstName,
          ).map(student => {
            const label = getSortedUserLabel(
              student,
              preferenceSorting ?? UserListSorting.FirstName,
            );
            return (
              <Option key={student.id} value={student.id} text={label}>
                {label}
              </Option>
            );
          })}
        </Dropdown>
      </div>
    </div>
  );

  const footerCancelButton = (
    <Button onClick={handleCancel}>
      {t('cancel', { defaultValue: 'Abbrechen' })}
    </Button>
  );
  const footerConfirmButton = (
    <Button appearance="primary" disabled={!isValid()} onClick={handleConfirm}>
      {t('confirm', { defaultValue: 'Bestätigen' })}
    </Button>
  );

  // ResponsiveDialog handles all breakpoints itself (centered card on tablet/desktop,
  // full-screen on phones ≤599px), so no separate mobile modal is needed.
  return (
    <ResponsiveDialog
      data-testid="NewClassServiceDialog"
      open={isOpen}
      header={headerText}
      className="newClassServiceDialog"
      secondaryAction={footerCancelButton}
      primaryAction={footerConfirmButton}
    >
      {contentElement}
    </ResponsiveDialog>
  );
};
